import React, { useState } from 'react';
import { MergeTag, PersonalizationService } from '../../services/personalizationService';

interface MergeTagSelectorProps {
  customFields?: any[];
  onTagSelect: (tag: string) => void;
  className?: string;
}

const MergeTagSelector: React.FC<MergeTagSelectorProps> = ({ 
  customFields = [], 
  onTagSelect,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Get all available tags
  const availableTags = PersonalizationService.getAvailableTags(customFields);
  
  // Group tags by category
  const standardTags = availableTags.filter(tag => 
    PersonalizationService.STANDARD_TAGS.some(stdTag => stdTag.id === tag.id)
  );
  
  const customTags = availableTags.filter(tag => 
    !PersonalizationService.STANDARD_TAGS.some(stdTag => stdTag.id === tag.id)
  );
  
  const handleTagSelect = (tag: MergeTag) => {
    onTagSelect(tag.syntax);
    setIsOpen(false);
  };
  
  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <svg className="-ml-0.5 mr-1 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M15.312 11.424a5.5 5.5 0 01-8.624 1.524 5.5 5.5 0 011.524-8.624 5.5 5.5 0 018.624 1.524 5.5 5.5 0 01-1.524 8.624zM12 17a1 1 0 100-2 1 1 0 000 2zm0-14a1 1 0 100 2 1 1 0 000-2zm-7 7a1 1 0 100 2 1 1 0 000-2zm14 0a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
        </svg>
        Merge Tags
      </button>
      
      {isOpen && (
        <div className="origin-top-right absolute right-0 mt-1 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
          <div className="py-1">
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Standard Tags
            </div>
            
            {standardTags.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => handleTagSelect(tag)}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                title={tag.description}
              >
                <div className="font-medium">{tag.name}</div>
                <div className="text-xs text-gray-500">{tag.syntax}</div>
              </button>
            ))}
            
            {customTags.length > 0 && (
              <>
                <div className="border-t border-gray-200 my-1"></div>
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Custom Fields
                </div>
                
                {customTags.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleTagSelect(tag)}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    title={tag.description}
                  >
                    <div className="font-medium">{tag.name}</div>
                    <div className="text-xs text-gray-500">{tag.syntax}</div>
                  </button>
                ))}
              </>
            )}
            
            <div className="border-t border-gray-200 my-1"></div>
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Dynamic Content
            </div>
            
            <button
              type="button"
              onClick={() => {
                onTagSelect('{{#if condition}}content{{/if}}');
                setIsOpen(false);
              }}
              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
            >
              <div className="font-medium">Conditional Block</div>
              <div className="text-xs text-gray-500">{'{{#if condition}}'}content{'{{/if}}'}</div>
            </button>
            
            <button
              type="button"
              onClick={() => {
                onTagSelect('{{#each items}}content{{/each}}');
                setIsOpen(false);
              }}
              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
            >
              <div className="font-medium">Loop Block</div>
              <div className="text-xs text-gray-500">{'{{#each items}}'}content{'{{/each}}'}</div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MergeTagSelector;
